import { useMemo } from 'react';
import {
  useCompany,
  useFilter,
  useGetUnassignedTasks,
  useGetUnassignedTaskCount,
  useCurrentUserResource,
  usePermissions,
} from '@dispatch/shared/hooks';
import { TaskPeril, ScheduleFilters } from '@dispatch/shared/types';
import { SchedulerDateRange } from '../types/scheduler.types';
import { useScheduleDataInfinite } from './useScheduleDataInfinite';
import { mapSchedulerFilters } from './mapSchedulerFilters';
import { format } from 'date-fns';

/** Queues excluded from unassigned task grid (matches v1 TasksListPage behavior) */
const EXCLUDED_UNASSIGNED_QUEUES = ['cancelled_q', 'wip_q', 'egress'];

/**
 * Converts perils array to a map keyed by peril code
 */
const perilDataMap = (perils: TaskPeril[] | undefined) =>
  (perils || []).reduce(
    (acc: { [code: string]: TaskPeril }, peril: TaskPeril) => {
      acc[peril.code] = peril;
      return acc;
    },
    {}
  );

interface UseSchedulerDataOptions {
  dateRange: SchedulerDateRange;
  timeZone: string;
  enabled?: boolean;
}

/**
 * Hook to fetch all data needed for the scheduler view
 *
 * Combines:
 * - Schedule data (resources, events, calendars)
 * - Unassigned tasks
 * - Company data (perils, reschedule reasons, etc.)
 * - User permissions
 */
export function useSchedulerData({
  dateRange,
  timeZone,
  enabled = true,
}: UseSchedulerDataOptions) {
  const { permissions } = usePermissions();
  const { isAdjuster, resourceId: currentUserResourceId } =
    useCurrentUserResource();
  const { filters, quickFilters } = useFilter();
  const { perils, rescheduleReasons, company } = useCompany();

  // Memoize peril map to avoid creating new references every render
  const perilMap = useMemo(() => perilDataMap(perils), [perils]);

  // Build schedule filters by merging taskFilters and resourceFilters from context,
  // remapping UI filter names to API-expected keys.
  const scheduleFilters = useMemo<ScheduleFilters>(
    () =>
      mapSchedulerFilters({
        taskFilters: filters?.taskFilters || {},
        resourceFilters: filters?.resourceFilters || {},
        attributeDefinitions: company?.attributeDefinition || [],
        dateRange,
        timeZone,
        status: quickFilters?.assignedStatus,
        pageSize: filters?.pageSize ?? 10,
        isAdjuster,
        currentUserResourceId,
      }),
    [
      filters,
      quickFilters?.assignedStatus,
      company?.attributeDefinition,
      dateRange,
      timeZone,
      isAdjuster,
      currentUserResourceId,
    ]
  );

  // Fetch schedule data with infinite scroll
  const scheduleData = useScheduleDataInfinite({
    filters: scheduleFilters,
    perilData: perilMap,
    enabled: enabled && !!scheduleFilters.scheduleStartDate,
  });

  // Fetch unassigned tasks only when the "Unassigned" quick filter is active
  const isUnassignedView = quickFilters?.assignedStatus === 'unassigned';
  const unassignedTaskFilters = {
    appointmentDateFrom: format(new Date(), 'yyyy/MM/dd'),
    status: 'unassigned',
    pageSize: 300,
  };

  const unassignedTasksData = useGetUnassignedTasks(
    unassignedTaskFilters,
    enabled && isUnassignedView
  );

  // Filter out cancelled/wip/egress tasks from unassigned results (matches v1 TasksListPage.tsx)
  const filteredUnassignedTasks = useMemo(
    () =>
      (unassignedTasksData.unassignedTasks || []).filter(
        (task) => !EXCLUDED_UNASSIGNED_QUEUES.includes(task?.targetQueue)
      ),
    [unassignedTasksData.unassignedTasks]
  );

  // Fetch unassigned task count from dedicated endpoint (matches TasksListPage behavior)
  const unassignedTaskCount = useGetUnassignedTaskCount();

  // Extract event categories from company config
  const eventCategories = useMemo(() => {
    return company?.eventCategories || [];
  }, [company?.eventCategories]);

  // Check permissions using CASL ability
  const canEditEvents = permissions?.can('update', 'task') ?? false;
  const canCreateEvents =
    permissions?.can('upsert', 'resource-events') ?? false;

  return {
    /**
     * Schedule data (resources, events, calendars from API)
     */
    data: {
      resources: scheduleData.data?.resources || [],
      events: scheduleData.data?.events || [],
      calendars: scheduleData.data?.calendars || [],
      unassignedTasks: filteredUnassignedTasks,
      /** Count of unassigned tasks for badge - from dedicated count endpoint */
      unassignedCount: unassignedTaskCount.data?.count || 0,
    },

    /**
     * Company configuration (perils, reasons, categories)
     */
    company: {
      perils: perils || [],
      perilMap,
      rescheduleReasons: rescheduleReasons || [],
      eventCategories,
    },

    /**
     * User permissions and role flags
     */
    permissions: {
      ability: permissions,
      isAdjuster,
      canEditEvents,
      canCreateEvents,
    },

    /**
     * Loading and fetching states
     */
    loading: {
      /** True when any data is loading */
      isLoading:
        scheduleData.isLoading ||
        unassignedTasksData.unassignedTasksStatus === 'loading',
      /** True when schedule data is loading or not yet loaded */
      isLoadingSchedule:
        scheduleData.isLoading ||
        scheduleData.status === 'loading' ||
        (scheduleData.isFetching && !scheduleData.data?.resources?.length),
      /** True when unassigned tasks are loading */
      isLoadingUnassignedTasks:
        unassignedTasksData.unassignedTasksStatus === 'loading',
      /** True when any background fetch is in progress */
      isFetching: scheduleData.isFetching,
      /** True when loading more resources via infinite scroll */
      isFetchingNextPage: scheduleData.isFetchingNextPage,
    },

    /**
     * Infinite scroll controls
     */
    pagination: {
      hasNextPage: scheduleData.hasNextPage,
      loadMoreResources: scheduleData.loadMore,
    },

    /**
     * Data refresh functions
     */
    refetch: {
      schedule: scheduleData.refetch,
      unassignedTasks: unassignedTasksData.fetchUnassignedTasks,
      unassignedCount: unassignedTaskCount.refetch,
      all: () => {
        scheduleData.refetch?.();
        unassignedTasksData.fetchUnassignedTasks?.();
        unassignedTaskCount.refetch?.();
      },
    },
  };
}
